import * as CL from '@design-system/component-library';
import {
  broadbandMsg,
  changeOrderMsg,
  elisaDevicesServiceMsg,
  newOrderMsg,
  orderTypeMsg,
  otherProductsMsg,
  phoneSubscriptionMsg,
  productsIncludedMsg,
  purchasedDeviceMsg,
  serviceMsg,
  statusMsg,
  t,
} from '../../common/i18n/index.js';
import { CustomerOrderProductType, CustomerOrderStatus, CustomerOrderType } from '../../generated/api/models.js';
import { TableUrlParams } from '../Table/index.js';
import { getCustomerOrderStatusInfo } from './CustomerOrderDetailsUtil.js';
import type {
  CustomerOrderAggregationsResponse,
  OrderTypeAggregations,
  ProductTypeAggregations,
} from '../../generated/api/models.js';
import type { SearchFilterGroup } from '../Table/SearchFilters.js';

const SEPARATOR = '|';

const orderedCustomerOrderStatuses = [
  CustomerOrderStatus.PENDING_APPROVAL,
  CustomerOrderStatus.IN_PROGRESS,
  CustomerOrderStatus.PARTIALLY_COMPLETED,
  CustomerOrderStatus.COMPLETED,
  CustomerOrderStatus.PENDING,
  CustomerOrderStatus.FAILED,
  CustomerOrderStatus.PARTIALLY_FAILED,
  CustomerOrderStatus.NEW,
  CustomerOrderStatus.SCHEDULED,
  CustomerOrderStatus.CANCELLED,
];

export enum CustomerOrderFilterType {
  PRODUCT_TYPE = 'productType',
  ORDER_TYPE = 'customerOrderType',
}

// Statuses hidden as standalone filter entries
const groupedStatuses = new Set([
  CustomerOrderStatus.FAILED,
  CustomerOrderStatus.PARTIALLY_FAILED,
  CustomerOrderStatus.NEW,
  CustomerOrderStatus.SCHEDULED,
]);

const isInProgressStatus = (orderedStatus: CustomerOrderStatus): boolean =>
  orderedStatus === CustomerOrderStatus.IN_PROGRESS;

const isPendingStatus = (s: CustomerOrderStatus) => s === CustomerOrderStatus.PENDING;

const getAggregationStatusItemsCount = (
  orderedStatus: CustomerOrderStatus,
  aggregations?: CustomerOrderAggregationsResponse
): number => aggregations?.status.find(aggregationStatus => aggregationStatus.name === orderedStatus)?.count ?? 0;

const getGroupedItemsCount = (
  orderStatus: CustomerOrderStatus,
  aggregations?: CustomerOrderAggregationsResponse
): number => {
  if (isInProgressStatus(orderStatus)) {
    // IN_PROGRESS is combined with FAILED and PARTIALLY_FAILED statuses under the same filter.
    return (
      getAggregationStatusItemsCount(orderStatus, aggregations) +
      getAggregationStatusItemsCount(CustomerOrderStatus.FAILED, aggregations) +
      getAggregationStatusItemsCount(CustomerOrderStatus.PARTIALLY_FAILED, aggregations)
    );
  }
  if (isPendingStatus(orderStatus)) {
    // PENDING is combined with NEW and SCHEDULED (OEC-specific pre-processing states).
    return (
      getAggregationStatusItemsCount(orderStatus, aggregations) +
      getAggregationStatusItemsCount(CustomerOrderStatus.NEW, aggregations) +
      getAggregationStatusItemsCount(CustomerOrderStatus.SCHEDULED, aggregations)
    );
  }
  return getAggregationStatusItemsCount(orderStatus, aggregations);
};

const getGroupedStatusValue = (orderStatus: CustomerOrderStatus): string => {
  if (isInProgressStatus(orderStatus)) {
    return `${CustomerOrderStatus.IN_PROGRESS}|${CustomerOrderStatus.FAILED}|${CustomerOrderStatus.PARTIALLY_FAILED}`;
  }
  if (isPendingStatus(orderStatus)) {
    return `${CustomerOrderStatus.PENDING}|${CustomerOrderStatus.NEW}|${CustomerOrderStatus.SCHEDULED}`;
  }
  return orderStatus;
};

export const getStatusFilterGroup = (
  aggregations?: CustomerOrderAggregationsResponse,
  status?: string
): SearchFilterGroup => {
  const items = orderedCustomerOrderStatuses
    .filter(orderedStatus => aggregations?.status.some(aggregationStatus => aggregationStatus.name === orderedStatus))
    .map(orderStatus => ({
      checked: status?.split(SEPARATOR).includes(orderStatus),
      itemsCount: getGroupedItemsCount(orderStatus, aggregations),
      label: () => getCustomerOrderStatusInfo(orderStatus).text,
      value: getGroupedStatusValue(orderStatus),
    }))
    .filter(item => !groupedStatuses.has(item.value as CustomerOrderStatus));

  return {
    label: () => t.ASQT(statusMsg),
    value: TableUrlParams.STATUS,
    items: items ?? [],
  };
};

const getProductTypeFilterGroup = (
  productTypeAggregations?: ProductTypeAggregations,
  productType = ''
): SearchFilterGroup => {
  const enabledPaymentTypeFilters = productType.split(SEPARATOR);
  return {
    label: () => t.BIS7(productsIncludedMsg),
    headerToolTip: (
      <CL.Tooltip
        triggerElement={<CL.Icon icon="information" size="s" />}
        placement="right"
        i18n_tooltip_contentText={t.KXZI('Note that one order may contain many different products.')}
      />
    ),
    value: CustomerOrderFilterType.PRODUCT_TYPE,
    items: [
      {
        label: () => t.R6AR(phoneSubscriptionMsg),
        checked: enabledPaymentTypeFilters.includes(CustomerOrderProductType.VOICE),
        value: CustomerOrderProductType.VOICE,
        itemsCount: productTypeAggregations?.voice,
      },
      {
        label: () => t.FV4B(broadbandMsg),
        checked: enabledPaymentTypeFilters.includes(CustomerOrderProductType.BROADBAND),
        value: CustomerOrderProductType.BROADBAND,
        itemsCount: productTypeAggregations?.broadband,
      },
      {
        label: () => t.JJM2(elisaDevicesServiceMsg),
        checked: enabledPaymentTypeFilters.includes(CustomerOrderProductType.EPP),
        value: CustomerOrderProductType.EPP,
        itemsCount: productTypeAggregations?.epp,
      },
      {
        label: () => t.QL8F(purchasedDeviceMsg),
        checked: enabledPaymentTypeFilters.includes(CustomerOrderProductType.DEVICE),
        value: CustomerOrderProductType.DEVICE,
        itemsCount: productTypeAggregations?.device,
      },
      {
        label: () => t.CDME(serviceMsg),
        checked: enabledPaymentTypeFilters.includes(CustomerOrderProductType.SERVICE),
        value: CustomerOrderProductType.SERVICE,
        itemsCount: productTypeAggregations?.service,
      },
      {
        label: () => t.K2PG(otherProductsMsg),
        checked: enabledPaymentTypeFilters.includes(CustomerOrderProductType.OTHER),
        value: CustomerOrderProductType.OTHER,
        itemsCount: productTypeAggregations?.other,
      },
    ],
  };
};

const getOrderTypeFilterGroup = (orderTypeAggregations?: OrderTypeAggregations, orderType = ''): SearchFilterGroup => {
  const orderTypeFilters = orderType.split(SEPARATOR);
  return {
    label: () => t.QDKP(orderTypeMsg),
    value: CustomerOrderFilterType.ORDER_TYPE,
    items: [
      {
        label: () => t.PTQ7(newOrderMsg),
        checked: orderTypeFilters.includes(CustomerOrderType.NEW_ORDER),
        value: CustomerOrderType.NEW_ORDER,
        itemsCount: orderTypeAggregations?.newOrder,
      },
      {
        label: () => t.PZ9O(changeOrderMsg),
        checked: orderTypeFilters.includes(CustomerOrderType.CHANGE_REQUEST),
        value: CustomerOrderType.CHANGE_REQUEST,
        itemsCount: orderTypeAggregations?.changeRequest,
      },
    ],
  };
};

export const getSearchFilterGroups = (
  aggregations?: CustomerOrderAggregationsResponse,
  status?: string,
  productType = '',
  orderType = '',
  isAdmin = true
): SearchFilterGroup[] => {
  // Only show filters to admin users
  if (!isAdmin) {
    return [];
  }

  const statusFilterGroups = [
    getStatusFilterGroup(aggregations, status),
    getProductTypeFilterGroup(aggregations?.productTypeAggregations, productType),
    getOrderTypeFilterGroup(aggregations?.orderTypeAggregations, orderType),
  ];
  return statusFilterGroups.filter(
    (filterGroup?: SearchFilterGroup): filterGroup is SearchFilterGroup => filterGroup !== undefined
  );
};
